import { useEffect, useState } from "react";
import api from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { Save, Loader2, Gavel, Plus, Check, BookOpen, Calculator } from "lucide-react";

export default function ClaimsPanel({ caseData, allClaims, onSaved }) {
  const [selected, setSelected] = useState(() => {
    const map = {};
    (caseData.claims || []).forEach((c) => { map[c.id] = c; });
    return map;
  });
  const [selectedJuris, setSelectedJuris] = useState(new Set(caseData.selected_jurisprudencia || []));
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [jurisOpen, setJurisOpen] = useState(false);
  const [activeClaim, setActiveClaim] = useState(null);
  const [jurisList, setJurisList] = useState([]);
  const [loadingJuris, setLoadingJuris] = useState(false);

  const toggleClaim = (claim, checked) => {
    setSelected((s) => {
      const next = { ...s };
      if (checked) next[claim.id] = { id: claim.id, label: claim.label, valor_estimado: "" };
      else delete next[claim.id];
      return next;
    });
  };

  const setValor = (id, valor) => {
    setSelected((s) => ({ ...s, [id]: { ...s[id], valor_estimado: valor } }));
  };

  const openJuris = async (claim) => {
    setActiveClaim(claim);
    setJurisOpen(true);
    setLoadingJuris(true);
    try {
      const res = await api.get(`/jurisprudence`, { params: { claim: claim.id } });
      setJurisList(res.data);
    } catch (e) {
      toast.error("Falha ao buscar jurisprudência");
    } finally {
      setLoadingJuris(false);
    }
  };

  const toggleJuris = (id) => {
    setSelectedJuris((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const claims = Object.values(selected);
      const valorCausa = total > 0 ? total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "";
      await api.put(`/cases/${caseData.id}`, {
        claims,
        selected_jurisprudencia: Array.from(selectedJuris),
        valor_causa: valorCausa,
        status: "pedidos",
      });
      toast.success("Pedidos salvos");
      onSaved({ ...caseData, claims, selected_jurisprudencia: Array.from(selectedJuris), valor_causa: valorCausa });
    } catch (e) {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const parseBRL = (s) => {
    let v = (s || "").replace(/[^\d.,-]/g, "").trim();
    if (!v) return 0;
    if (v.includes(",")) v = v.replace(/\./g, "").replace(",", ".");
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };
  const calcular = async () => {
    const ids = Object.keys(selected);
    if (!ids.length) { toast.error("Selecione ao menos um pedido para calcular"); return; }
    setCalculating(true);
    try {
      const res = await api.post(`/cases/${caseData.id}/calculate`, { claim_ids: ids });
      const data = res.data;
      setSelected((s) => {
        const next = { ...s };
        Object.entries(data).forEach(([id, r]) => {
          if (next[id]) next[id] = { ...next[id], valor_estimado: r.valor || next[id].valor_estimado, memoria: r.memoria || "" };
        });
        return next;
      });
      toast.success("Valores calculados automaticamente — revise as estimativas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao calcular");
    } finally {
      setCalculating(false);
    }
  };

  const total = Object.values(selected).reduce((acc, c) => acc + parseBRL(c.valor_estimado), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur py-2 z-10">
        <div>
          <p className="text-sm font-medium">Valor da causa (liquidação assistida)</p>
          <p className="font-display text-2xl font-extrabold text-amber-700" data-testid="total-estimated">
            {total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
          </p>
          <p className="text-xs text-muted-foreground">Soma automática dos pedidos — usada como valor da causa na petição.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="calc-btn" onClick={calcular} disabled={calculating}
            variant="outline" className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
            {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Calcular valores
          </Button>
          <Button data-testid="save-claims-btn" onClick={save} disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar pedidos
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {allClaims.map((claim) => {
          const checked = !!selected[claim.id];
          const jurisCount = claim.sumulas?.filter((s) => selectedJuris.has(s)).length || 0;
          return (
            <div key={claim.id} data-testid={`claim-row-${claim.id}`}
              className={`border rounded-xl p-4 transition-colors ${checked ? "border-amber-300 bg-amber-50/40" : "bg-white"}`}>
              <div className="flex items-center gap-3">
                <Checkbox data-testid={`claim-${claim.id}-checkbox`} checked={checked}
                  onCheckedChange={(v) => toggleClaim(claim, v)} />
                <div className="flex-1 min-w-0">
                  <label className="font-semibold text-sm cursor-pointer" onClick={() => toggleClaim(claim, !checked)}>
                    {claim.label}
                  </label>
                </div>
                {checked && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" data-testid={`juris-btn-${claim.id}`}
                      onClick={() => openJuris(claim)} className="gap-1.5 h-9">
                      <Gavel className="h-3.5 w-3.5" /> Jurisprudência
                      {jurisCount > 0 && <Badge className="bg-blue-600 text-white ml-1 h-5 px-1.5">{jurisCount}</Badge>}
                    </Button>
                    <div className="w-40">
                      <Input data-testid={`claim-${claim.id}-valor`} placeholder="R$ 0,00"
                        value={selected[claim.id]?.valor_estimado || ""}
                        onChange={(e) => setValor(claim.id, e.target.value)} className="bg-white h-9 text-sm" />
                    </div>
                  </div>
                )}
              </div>
              {checked && selected[claim.id]?.memoria && (
                <p data-testid={`memoria-${claim.id}`} className="text-[11px] text-muted-foreground mt-2 pl-7 leading-relaxed">
                  <span className="font-semibold text-amber-700">Memória de cálculo: </span>{selected[claim.id].memoria}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={jurisOpen} onOpenChange={setJurisOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" /> Jurisprudência — {activeClaim?.label}
            </DialogTitle>
            <DialogDescription>Súmulas e OJs reais do TST. Selecione as que deseja citar na fundamentação.</DialogDescription>
          </DialogHeader>
          {loadingJuris ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" /></div>
          ) : jurisList.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma jurisprudência curada para este pedido.</p>
          ) : (
            <div className="space-y-3">
              {jurisList.map((j) => {
                const isSel = selectedJuris.has(j.id);
                return (
                  <div key={j.id} data-testid={`juris-item-${j.id}`}
                    className={`border rounded-lg p-4 ${isSel ? "border-blue-400 bg-blue-50/50" : "bg-white"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <Badge className="bg-blue-600 text-white mb-2">{j.tipo} nº {j.numero} — {j.tribunal}</Badge>
                        <p className="font-semibold text-sm">{j.titulo}</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{j.ementa}</p>
                      </div>
                      <Button size="sm" variant={isSel ? "default" : "outline"}
                        data-testid={`toggle-juris-${j.id}`} onClick={() => toggleJuris(j.id)}
                        className={isSel ? "bg-blue-600 hover:bg-blue-700 gap-1" : "gap-1"}>
                        {isSel ? <><Check className="h-3.5 w-3.5" /> Selecionada</> : <><Plus className="h-3.5 w-3.5" /> Anexar</>}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
